import { EyeOutlined, UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Table, Typography, Spin, Tag, Button, Card, Space, Modal, message, List, Divider, Input, Avatar, Upload } from "antd";

const { Text } = Typography;

const PayoutRequest = () => {
  const [loading, setLoading] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [paginationLimit, setPaginationLimit] = useState(10);
  const [pageSizeOptions] = useState(["10", "20", "50", "100"]);

  const [userPayoutHistory, setUserPayoutHistory] = useState([]);
  const [userTotalCount, setUserTotalCount] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [userPaginationLimit, setUserPaginationLimit] = useState(10);
  const [isMarkPaidModalVisible, setIsMarkPaidModalVisible] = useState(false);
  const [transactionProof, setTransactionProof] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [currentDetails, setCurrentDetails] = useState(null);

  const handleMarkAsPaid = async () => {
    if (!transactionProof) {
      message.error("Please upload transaction proof");
      return;
    }
    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append("transactionProof", transactionProof);
      const response = await axios.post(`${process.env.REACT_APP_ADMIN_API_URL}payout/admin/pay-payout-request?payoutRequestId=${selectedPayout}`, formData, {
        headers: {
          "x-product-type": "PIP_GRID",
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("userToken")}`,
        },
      });
      if (response.data.success) {
        message.success("Payout marked as paid successfully");
        setIsMarkPaidModalVisible(false);
        setIsModalVisible(false);
        fetchPayoutHistory();
      }
    } catch (error) {
      console.error("Error marking as paid:", error);
      message.error(error.response?.data?.message || "Failed to mark as paid");
    } finally {
      setIsSubmitting(false);
    }
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    const isLt5M = file.size / 1024 / 1024 < 5; // 5MB limit

    if (!isImage) {
      message.error("You can only upload image files (JPG, PNG, etc.)!");
      return Upload.LIST_IGNORE;
    }

    if (!isLt5M) {
      message.error("Image must be smaller than 5MB!");
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  useEffect(() => {
    fetchPayoutHistory();
  }, [page, paginationLimit]);

  useEffect(() => {
    if (isModalVisible) {
      fetchUserPayoutHistory();
    }
  }, [isModalVisible]);

  const fetchPayoutHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}payout/admin/payout-history?payoutStatus=pending&page=${page}&limit=${paginationLimit}`,
        {
          headers: {
            "x-product-type": "PIP_GRID",
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      if (response?.data?.success) {
        const data = response?.data?.payload?.data.map((item) => ({
          key: item?._id,
          id: item?._id,
          createdAt: dayjs(item?.createdAt).format("DD-MM-YYYY"),
          name: item?.user?.fullName,
          email: item?.user.email,
          phoneNumber: item?.user?.phoneNumber,
          totalCommission: item?.payoutAmount,
          payoutMethod: item?.payoutMethod,
          commissionType: item?.commissionType,
          bankDetails: item?.bankDetails,
          cryptoDetails: item?.cryptoDetails
        }));
        setPayoutHistory(data || []);
        setTotalCount(response?.data?.payload?.count || 0);
      }
    } catch (error) {
      console.error("Error fetching payout history:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPayoutHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}payout/admin/payout-users?payoutRequestId=${selectedPayout}&page=${userPage}&limit=${userPaginationLimit}`,
        {
          headers: {
            "x-product-type": "PIP_GRID",
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      if (response?.data?.success) {
        const data = response?.data?.payload?.commissions?.map((item) => ({
          key: item?._id,
          id: item?._id,
          name: item?.name,
          email: item.email,
          commissionAmount: item?.commissionAmount,
          phoneNumber: item?.phone,
        }));
        setUserPayoutHistory(data || []);
        setUserTotalCount(response?.data?.payload?.count || 0);
      }
    } catch (error) {
      console.error("Error fetching payout history:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
    },
    {
      title: "Total Commission",
      dataIndex: "totalCommission",
      key: "totalCommission",
    },
    {
      title: "Payout Method",
      dataIndex: "payoutMethod",
      key: "payoutMethod",
    },
    { title: "Commission Type", dataIndex: "commissionType", key: "commissionType" },
    {
      title: "Bank Details",
      key: "bankDetails",
      align: "center",
      render: (_, record) => {
        return (
          <Button
            type="link"
            onClick={() => {
              setCurrentDetails(record);
              setIsDetailsModalVisible(true);
            }}
          >
            View Details
          </Button>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => {
        return (
          <EyeOutlined
            style={{ cursor: "pointer", fontSize: "16px", color: "#1890ff" }}
            onClick={() => {
              setSelectedPayout(record?.id);
              setIsModalVisible(true);
            }}
          />
        );
      },
    },
  ];

  const userColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
    },
    {
      title: "Commission Amount",
      dataIndex: "commissionAmount",
      key: "commissionAmount",
    },
  ];

  return (
    <div className="p-4">
      <Spin spinning={loading} size="large">
        <Typography.Title level={3} className="page-title">
          Payout History
        </Typography.Title>
        <Table
          columns={columns}
          dataSource={payoutHistory}
          loading={loading}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: paginationLimit,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions,
            onChange: (page, pageSize) => {
              setPage(page);
              setPaginationLimit(pageSize);
            },
          }}
        />
        <Modal
          title={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
              <span>Payout Details</span>
              <Button type="primary" onClick={() => setIsMarkPaidModalVisible(true)} style={{ marginRight: 40 }}>
                Mark as Paid
              </Button>
            </div>
          }
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
            setTransactionProof(null);
          }}
          footer={null}
          width={800}
        >
          {selectedPayout && (
            <div>
              <Table
                columns={userColumns}
                dataSource={userPayoutHistory || []}
                rowKey="userId"
                pagination={{
                  current: userPage,
                  pageSize: userPaginationLimit,
                  total: userTotalCount,
                  onChange: (page, pageSize) => {
                    setPage(page);
                  },
                }}
              />
            </div>
          )}
        </Modal>

        {/* Mark as Paid Modal */}
        <Modal
          title="Mark as Paid"
          open={isMarkPaidModalVisible}
          onCancel={() => setIsMarkPaidModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setIsMarkPaidModalVisible(false)}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" onClick={handleMarkAsPaid} loading={isSubmitting} disabled={!transactionProof}>
              Submit
            </Button>,
          ]}
        >
          <div
            onDrop={(e) => {
              e.preventDefault();
              if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                const file = e.dataTransfer.files[0];
                if (file.type.startsWith("image/")) {
                  setTransactionProof(file);
                } else {
                  message.error("You can only upload image files!");
                }
              }
            }}
            onDragOver={(e) => e.preventDefault()}
            style={{
              border: "2px dashed #d9d9d9",
              borderRadius: "8px",
              padding: "16px",
              textAlign: "center",
              backgroundColor: "#fafafa",
              cursor: "pointer",
              transition: "border-color 0.3s",
              marginBottom: "16px",
              minHeight: "140px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ":hover": {
                borderColor: "#1890ff",
              },
            }}
          >
            <Upload.Dragger
              name="transactionProof"
              showUploadList={false}
              beforeUpload={beforeUpload}
              onChange={(info) => info.file && setTransactionProof(info.file)}
              style={{
                background: "transparent",
                border: "none",
              }}
            >
              <div style={{ padding: "8px", width: "100%" }}>
                {transactionProof ? (
                  <div style={{ position: "relative" }}>
                    <img
                      src={URL.createObjectURL(transactionProof)}
                      alt="transaction-proof"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "200px",
                        borderRadius: "4px",
                        border: "1px solid #f0f0f0",
                        padding: "8px",
                        backgroundColor: "#fff",
                      }}
                    />
                    <div style={{ marginTop: "16px" }}>
                      <Button
                        type="primary"
                        danger
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTransactionProof(null);
                        }}
                        style={{ marginRight: "8px" }}
                      >
                        Change Image
                      </Button>
                    
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="ant-upload-drag-icon" style={{ marginBottom: "8px" }}>
                      <UploadOutlined style={{ fontSize: "32px", color: "#1890ff" }} />
                    </p>
                    <p className="ant-upload-text" style={{ marginBottom: "4px", fontSize: "14px" }}>
                      Click or drag file to upload
                    </p>
                  </div>
                )}
              </div>
            </Upload.Dragger>
          </div>
        </Modal>

        {/* Bank/Crypto Details Modal */}
        <Modal
          title="Payment Details"
          open={isDetailsModalVisible}
          onCancel={() => setIsDetailsModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setIsDetailsModalVisible(false)}>
              Close
            </Button>
          ]}
          width={600}
        >
          <div style={{ padding: '16px' }}>
            {currentDetails?.payoutMethod === "BANK" ? (
              <>
                <h3 style={{ marginBottom: '16px', color: '#1d39c4' }}>Bank Transfer Details</h3>
                <div style={{ 
                  background: '#f9f9f9', 
                  padding: '20px', 
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex' }}>
                      <span style={{ minWidth: '120px', color: '#666' }}>Bank Name:</span>
                      <span style={{ fontWeight: 500 }}>{currentDetails.bankDetails?.bankName || 'N/A'}</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ minWidth: '120px', color: '#666' }}>Account Name:</span>
                      <span style={{ fontWeight: 500 }}>{currentDetails.bankDetails?.accountName || 'N/A'}</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ minWidth: '120px', color: '#666' }}>Account Number:</span>
                      <span style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '15px',
                        letterSpacing: '0.5px',
                        backgroundColor: '#f0f0f0',
                        padding: '2px 8px',
                        borderRadius: '4px'
                      }}>
                        {currentDetails.bankDetails?.accountNumber || 'N/A'}
                      </span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ minWidth: '120px', color: '#666' }}>IFSC Code:</span>
                      <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                        {currentDetails.bankDetails?.ifscCode || 'N/A'}
                      </span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ minWidth: '120px', color: '#666' }}>City:</span>
                      <span>{currentDetails.bankDetails?.city || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </>
            ) : currentDetails?.cryptoDetails ? (
              <>
                <h3 style={{ marginBottom: '16px', color: '#1d39c4' }}>Crypto Wallet Details</h3>
                <div style={{ 
                  background: '#f9f9f9', 
                  padding: '20px', 
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex' }}>
                      <span style={{ minWidth: '120px', color: '#666' }}>Wallet Name:</span>
                      <span style={{ fontWeight: 500 }}>{currentDetails.cryptoDetails?.walletName || 'N/A'}</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ minWidth: '120px', color: '#666' }}>Name:</span>
                      <span style={{ fontWeight: 500 }}>{currentDetails.cryptoDetails?.name || 'N/A'}</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ minWidth: '120px', color: '#666' }}>Wallet Address:</span>
                      <span style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '15px',
                        letterSpacing: '0.5px',
                        backgroundColor: '#f0f0f0',
                        padding: '8px',
                        borderRadius: '4px',
                        wordBreak: 'break-all'
                      }}>
                        {currentDetails.cryptoDetails?.walletAddress || 'N/A'}
                      </span>
                    </div>
                  </div>
                  
                  {currentDetails.cryptoDetails.qrCode && (
                    <div style={{
                      alignSelf: 'center',
                      textAlign: 'center',
                      marginTop: '16px'
                    }}>
                      <div style={{ 
                        padding: '16px', 
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        display: 'inline-block',
                        border: '1px solid #f0f0f0',
                        marginBottom: '8px'
                      }}>
                        <img 
                          src={currentDetails.cryptoDetails.qrCode} 
                          alt="Wallet QR Code" 
                          style={{ 
                            width: '180px', 
                            height: '180px',
                            display: 'block'
                          }}
                        />
                      </div>
                      <div style={{ 
                        fontSize: '13px',
                        color: '#666',
                        maxWidth: '220px',
                        margin: '0 auto',
                        lineHeight: '1.4'
                      }}>
                        Scan this QR code to send cryptocurrency to this wallet
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={{ 
                textAlign: 'center',
                padding: '40px 20px',
                color: '#999'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>ℹ️</div>
                <p style={{ fontSize: '16px', margin: 0 }}>No payment details available</p>
              </div>
            )}
          </div>
        </Modal>
      </Spin>
    </div>
  );
};

export default PayoutRequest;
